import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Suspense, lazy } from 'react'
import Layout from './components/Layout'
import { AuthProvider, useAuth } from './contexts/AuthContext'

// Loading component for lazy-loaded pages
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
      <p className="mt-4 text-gray-600">Loading...</p>
    </div>
  </div>
)

// Core pages - loaded immediately (auth, dashboard)
import LoginPage from './pages/LoginPage'
import DashboardPage from './pages/DashboardPage'

// Lazy-loaded pages - split into separate chunks
const LicensesPage = lazy(() => import('./pages/LicensesPage'))
const AuditLogsPage = lazy(() => import('./pages/AuditLogsPage'))
const UsageDashboardPage = lazy(() => import('./pages/UsageDashboardPage'))
const PlanManagement = lazy(() => import('./pages/PlanManagement'))
const AIModelManagementPage = lazy(() => import('./pages/AIModelManagementPage'))
const SystemHealthPage = lazy(() => import('./pages/SystemHealthPage'))
const CustomerInstancesPage = lazy(() => import('./pages/CustomerInstancesPage'))
const DeploymentManagement = lazy(() => import('./pages/DeploymentManagement'))
const ProveEyePage = lazy(() => import('./pages/ProveEyePage'))
const ProveEyeScansPage = lazy(() => import('./pages/ProveEyeScansPage'))
const ScanDetailsPage = lazy(() => import('./pages/ScanDetailsPage'))
const BackupStorageSettingsPage = lazy(() => import('./pages/BackupStorageSettingsPage'))
const DemoInstancesPage = lazy(() => import('./pages/DemoInstancesPage'))
const InstanceSettingsPage = lazy(() => import('./pages/InstanceSettingsPage'))
const ModelTiersPage = lazy(() => import('./pages/ModelTiersPage'))
const AgentRuntimesPage = lazy(() => import('./pages/AgentRuntimesPage'))
const ConsoleLogsPage = lazy(() => import('./pages/ConsoleLogsPage'))
const ScrapingProvidersPage = lazy(() => import('./pages/ScrapingProvidersPage'))
const DevEnvironmentPage = lazy(() => import('./pages/DevEnvironmentPage'))
const InstanceHealthPage = lazy(() => import('./pages/InstanceHealthPage'))
const OpenRouterAnalyticsPage = lazy(() => import('./pages/OpenRouterAnalyticsPage'))
const JevPage = lazy(() => import('./pages/JevPage'))

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: any) => {
        // Never retry on 401 (auth failure)
        if (error?.response?.status === 401 || error?.status === 401) return false
        return failureCount < 2
      },
    },
  },
})

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  
  // Wait for loading to complete before checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }
  
  if (!user) {
    return <Navigate to="/login" replace />
  }
  
  return <Layout>{children}</Layout>
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AuthProvider>
          <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/" element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            } />
            <Route path="/licenses" element={
              <ProtectedRoute>
                <LicensesPage />
              </ProtectedRoute>
            } />
            <Route path="/audit" element={
              <ProtectedRoute>
                <AuditLogsPage />
              </ProtectedRoute>
            } />
            <Route path="/usage" element={
              <ProtectedRoute>
                <UsageDashboardPage />
              </ProtectedRoute>
            } />
            <Route path="/plans" element={
              <ProtectedRoute>
                <PlanManagement />
              </ProtectedRoute>
            } />
            <Route path="/ai-models" element={
              <ProtectedRoute>
                <AIModelManagementPage />
              </ProtectedRoute>
            } />
            <Route path="/system-health" element={
              <ProtectedRoute>
                <SystemHealthPage />
              </ProtectedRoute>
            } />
            <Route path="/customer-instances" element={
              <ProtectedRoute>
                <CustomerInstancesPage />
              </ProtectedRoute>
            } />
            <Route path="/deployments" element={
              <ProtectedRoute>
                <DeploymentManagement />
              </ProtectedRoute>
            } />
            <Route path="/proveye" element={
              <ProtectedRoute>
                <ProveEyePage />
              </ProtectedRoute>
            } />
            <Route path="/proveye-scans" element={
              <ProtectedRoute>
                <ProveEyeScansPage />
              </ProtectedRoute>
            } />
            <Route path="/proveye/scan-details/:scanId" element={
              <ProtectedRoute>
                <ScanDetailsPage />
              </ProtectedRoute>
            } />
            <Route path="/backup-storage" element={
              <ProtectedRoute>
                <BackupStorageSettingsPage />
              </ProtectedRoute>
            } />
            <Route path="/demo-instances" element={
              <ProtectedRoute>
                <DemoInstancesPage />
              </ProtectedRoute>
            } />
            <Route path="/instance-settings/:instanceId" element={
              <ProtectedRoute>
                <InstanceSettingsPage />
              </ProtectedRoute>
            } />
            <Route path="/instance-settings" element={
              <ProtectedRoute>
                <InstanceSettingsPage />
              </ProtectedRoute>
            } />
            <Route path="/model-tiers" element={
              <ProtectedRoute>
                <ModelTiersPage />
              </ProtectedRoute>
            } />
            <Route path="/agent-runtimes" element={
              <ProtectedRoute>
                <AgentRuntimesPage />
              </ProtectedRoute>
            } />
            <Route path="/console-logs" element={
              <ProtectedRoute>
                <ConsoleLogsPage />
              </ProtectedRoute>
            } />
            <Route path="/scraping-providers" element={
              <ProtectedRoute>
                <ScrapingProvidersPage />
              </ProtectedRoute>
            } />
            <Route path="/dev-environment" element={
              <ProtectedRoute>
                <DevEnvironmentPage />
              </ProtectedRoute>
            } />
            <Route path="/instance-health" element={
              <ProtectedRoute>
                <InstanceHealthPage />
              </ProtectedRoute>
            } />
            <Route path="/openrouter-analytics" element={
              <ProtectedRoute>
                <OpenRouterAnalyticsPage />
              </ProtectedRoute>
            } />
            <Route path="/jev" element={
              <ProtectedRoute>
                <JevPage />
              </ProtectedRoute>
            } />
          </Routes>
          </Suspense>
        </AuthProvider>
      </Router>
    </QueryClientProvider>
  )
}

export default App 
